import { computed, nextTick, ref, toValue, type ComponentPublicInstance, type MaybeRefOrGetter } from "vue";
import { EMOJI_PICKER_POPOVER_OFFSET } from "@/src/design_system/button_and_controls/emoji_picker/constants/layout";
import { DS_POPOVER_PLACEMENTS } from "@/src/design_system/primitives/popover/types/props";
import { generateUUID } from "@/src/shared/utils/uuid";

interface PickerController {
  close: () => void;
}

interface UseEmojiPickerControllerOptions {
  disabled: MaybeRefOrGetter<boolean>;
  fastSelect: MaybeRefOrGetter<boolean>;
  onPrepare: () => void;
}

export const useEmojiPickerController = ({ disabled, fastSelect, onPrepare }: UseEmojiPickerControllerOptions) => {
  const isOpen = ref(false);
  const showFullPicker = ref(true);
  const activatorRef = ref<HTMLButtonElement>();
  const pickerRef = ref<PickerController>();
  const panelId = `ds-emoji-picker-panel-${generateUUID()}`;

  // RightStart has no room to grow into inside a narrow sidebar (e.g. the
  // activity/comments panel), and the popover has no flip fallback, so it would
  // just get clamped into an overlap with the activator instead of sitting beside it.
  const popoverPlacement = DS_POPOVER_PLACEMENTS.BottomStart;
  const popoverOffset = computed(() => (showFullPicker.value ? EMOJI_PICKER_POPOVER_OFFSET : undefined));

  const setActivatorRef = (
    component: ComponentPublicInstance | null,
    setPopoverRef: (element: HTMLElement | null) => void
  ): void => {
    const element = component?.$el as HTMLButtonElement | undefined;
    activatorRef.value = element;
    setPopoverRef(element ?? null);
  };

  const preparePicker = (): boolean => {
    if (toValue(disabled)) return false;

    onPrepare();
    showFullPicker.value = !toValue(fastSelect);
    return true;
  };

  const close = (): void => {
    pickerRef.value?.close();
  };

  const focus = (): void => {
    activatorRef.value?.focus();
  };

  const closeAndFocus = (): void => {
    close();
    void nextTick(focus);
  };

  const restoreActivatorFocus = (): void => {
    void nextTick(focus);
  };

  const onActivatorClick = (pickerIsOpen: boolean, toggle: () => void): void => {
    if (!pickerIsOpen && !preparePicker()) return;

    toggle();
  };

  const showAllEmojis = (): void => {
    showFullPicker.value = true;
  };

  return {
    closeAndFocus,
    focus,
    isOpen,
    onActivatorClick,
    panelId,
    pickerRef,
    popoverOffset,
    popoverPlacement,
    restoreActivatorFocus,
    setActivatorRef,
    showAllEmojis,
    showFullPicker,
  };
};
